/**
 * Training and running.
 *
 * Strength work is logged here; runs flow in from Apple Health rather than
 * being typed, so this screen shows them but does not ask for them.
 */
import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Card, Empty, Field, Icons, Sheet, Sparkline, fmt } from '../ui/bits';
import { exerciseHistory, exerciseOverview } from '../domain/strength';
import { exercises, notify, routines, runsRecent, saveExercise, saveRoutine, saveSession, sessionsRecent, settings, today } from '../store';
import { formatDuration, fromDay, nowIso } from '../lib/time';
import { newId } from '../lib/id';
import type { LoggedSet, Routine, WorkoutSession } from '../data/schema';

const M_PER_MI = 1609.344;

export function Train(props: { onStart: (routine?: Routine) => void }): JSX.Element {
  const rts = routines.value;
  const sessions = sessionsRecent.value;
  const runs = runsRecent.value;
  const dist = settings.value.distanceUnit;
  const [openExercise, setOpenExercise] = useState<string | null>(null);
  const overview = exerciseOverview(sessions);
  const lb = settings.value.weightUnit === 'lb';
  const showKg = (kg: number) => fmt(lb ? kg * 2.20462262185 : kg, 0);
  const nameOf = (id: string) => exercises.value.find((e) => e.id === id)?.name ?? 'Exercise';

  return (
    <>
      <Card title="Strength" flush>
        {overview.length === 0 ? (
          <Empty>Once sets are logged, each exercise shows its estimated 1RM trend and PRs here.</Empty>
        ) : (
          <div class="list">
            {overview.map((o) => (
              <button key={o.exerciseId} class="item" onClick={() => setOpenExercise(o.exerciseId)}>
                <div class="col grow">
                  <span class="item-title">{nameOf(o.exerciseId)}</span>
                  <span class="item-sub">{o.sessions} {o.sessions === 1 ? 'session' : 'sessions'} · best est. 1RM {o.bestE1rm !== null ? `${showKg(o.bestE1rm)} ${settings.value.weightUnit}` : '—'}</span>
                </div>
                <span class="readout readout-sm">{o.latestE1rm !== null ? showKg(o.latestE1rm) : '—'}<span class="unit"> {settings.value.weightUnit}</span></span>
              </button>
            ))}
          </div>
        )}
      </Card>
      {openExercise ? <ExerciseSheet exerciseId={openExercise} onClose={() => setOpenExercise(null)} /> : null}

      <Card title="Routines" action={<button class="btn btn-sm btn-ghost" onClick={() => props.onStart()}>New</button>}>
        {rts.length === 0 ? (
          <Empty mark={Icons.train}>
            No routines yet. Build one to track sets, reps and weight against it.
          </Empty>
        ) : (
          <div class="row wrap" style={{ gap: '6px' }}>
            {rts.map((r) => (
              <button key={r.id} class="chip" onClick={() => props.onStart(r)}>
                {r.name}
                <span class="dim">{r.exercises.length}</span>
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card title="Recent sessions" flush>
        {sessions.length === 0 ? (
          <Empty>Sessions you log will appear here with their volume.</Empty>
        ) : (
          <div class="list">
            {sessions.slice(0, 8).map((s) => (
              <div key={s.id} class="item">
                <div class="col grow">
                  <span class="item-title">{s.name}</span>
                  <span class="item-sub">
                    {fromDay(s.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    {' · '}{s.exercises.length} {s.exercises.length === 1 ? 'exercise' : 'exercises'} · {showKg(volumeKg(s))} {settings.value.weightUnit} volume
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Runs" flush action={<span class="label" style={{ paddingRight: '14px' }}>via Apple Health</span>}>
        {runs.length === 0 ? (
          <Empty mark={Icons.run}>
            Runs arrive from Apple Health once the automation is set up. See Settings.
          </Empty>
        ) : (
          <div class="list">
            {runs.slice(0, 8).map((r) => {
              const distance = dist === 'mi' ? r.distanceM / M_PER_MI : r.distanceM / 1000;
              const paceSec = r.distanceM > 0 ? r.durationSec / distance : 0;
              return (
                <div key={r.id} class="item">
                  <div class="col grow">
                    <span class="item-title">{fmt(distance, 2)} {dist}</span>
                    <span class="item-sub">
                      {fromDay(r.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      {' · '}{formatDuration(r.durationSec)}
                      {paceSec > 0 ? ` · ${pace(paceSec)}/${dist}` : ''}
                      {r.avgHr ? ` · ${r.avgHr} bpm` : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
}

function pace(secPerUnit: number): string {
  const m = Math.floor(secPerUnit / 60);
  const s = Math.round(secPerUnit % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function volumeKg(session: WorkoutSession): number {
  return session.exercises.reduce((sum, ex) =>
    sum + ex.sets.reduce((s, set) =>
      s + (set.warmup ? 0 : (set.weightKg ?? 0) * (set.reps ?? 0)), 0), 0);
}

// --------------------------------------------------------------------------
// One exercise over time
// --------------------------------------------------------------------------

function ExerciseSheet(props: { exerciseId: string; onClose: () => void }): JSX.Element {
  const history = exerciseHistory(sessionsRecent.value, props.exerciseId);
  const name = exercises.value.find((e) => e.id === props.exerciseId)?.name ?? 'Exercise';
  const unit = settings.value.weightUnit;
  const conv = (kg: number) => (unit === 'lb' ? kg * 2.20462262185 : kg);
  const prs = history.filter((h) => h.pr).length;
  const best = history.reduce<number | null>((acc, h) => (h.best && (acc === null || h.best.e1rm > acc) ? h.best.e1rm : acc), null);

  return (
    <Sheet title={name} onClose={props.onClose}>
      <div class="card">
        <div class="row-between" style={{ alignItems: 'flex-end' }}>
          <div class="col">
            <span class="label">Best est. 1RM</span>
            <span class="readout readout-xl">{best !== null ? fmt(conv(best), 0) : '—'}<span class="unit" style={{ fontSize: '14px' }}> {unit}</span></span>
          </div>
          <div class="col" style={{ alignItems: 'flex-end' }}>
            <span class="label">PRs</span>
            <span class="readout readout-md">{prs}</span>
          </div>
        </div>
        {history.length >= 2 ? (
          <div style={{ marginTop: '10px' }}>
            <span class="label">Est. 1RM by session</span>
            <Sparkline values={history.map((h) => (h.best ? conv(h.best.e1rm) : null))} />
          </div>
        ) : null}
        <p class="note" style={{ marginTop: '8px' }}>Epley estimate from the best working set. Sets over 12 reps are shown but do not set PRs.</p>
      </div>
      <div class="card list" style={{ padding: 0 }}>
        {[...history].reverse().map((h) => (
          <div key={h.sessionId} class="item">
            <div class="col grow">
              <span class="item-title">
                {h.best ? `${fmt(conv(h.best.weightKg), 0)} ${unit} × ${h.best.reps}` : '—'}
              </span>
              <span class="item-sub">
                {fromDay(h.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · {h.sets} sets · {fmt(conv(h.volumeKg), 0)} {unit} volume
              </span>
            </div>
            <div class="col" style={{ alignItems: 'flex-end', gap: '4px' }}>
              <span class="readout readout-sm">{h.best ? fmt(conv(h.best.e1rm), 0) : '—'}<span class="unit"> e1RM</span></span>
              {h.pr ? <span class="badge is-done">PR</span> : null}
            </div>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------
// Logging a session
// --------------------------------------------------------------------------

export function SessionSheet(props: { routine?: Routine | undefined; onClose: () => void }): JSX.Element {
  const [name, setName] = useState(props.routine?.name ?? 'Session');
  const known = exercises.value;
  const [rows, setRows] = useState<{ exerciseId: string; name: string; sets: LoggedSet[] }[]>(() =>
    (props.routine?.exercises ?? []).map((re) => ({
      exerciseId: re.exerciseId,
      name: known.find((e) => e.id === re.exerciseId)?.name ?? 'Exercise',
      sets: [],
    })),
  );
  const [newExercise, setNewExercise] = useState('');
  const [busy, setBusy] = useState(false);
  const unitIsLb = settings.value.weightUnit === 'lb';
  const toKg = (v: number) => (unitIsLb ? v / 2.20462262185 : v);
  const fromKg = (v: number) => (unitIsLb ? v * 2.20462262185 : v);

  const addExercise = async () => {
    const label = newExercise.trim();
    if (!label) return;
    let existing = known.find((e) => e.name.toLowerCase() === label.toLowerCase());
    if (!existing) {
      existing = { id: newId(), name: label, metric: 'weight_reps', createdAt: nowIso() };
      await saveExercise(existing);
    }
    setRows([...rows, { exerciseId: existing.id, name: existing.name, sets: [] }]);
    setNewExercise('');
  };

  const save = async () => {
    const withSets = rows.filter((r) => r.sets.length);
    if (!withSets.length) { notify('Log at least one set.', true); return; }
    setBusy(true);
    try {
      const session: WorkoutSession = {
        id: newId(), day: today.value, startedAt: nowIso(), endedAt: nowIso(),
        name: name.trim() || 'Session',
        ...(props.routine ? { routineId: props.routine.id } : {}),
        exercises: withSets.map((r) => ({ exerciseId: r.exerciseId, sets: r.sets })),
      };
      await saveSession(session);
      // A session built ad hoc is worth keeping as a routine to repeat.
      if (!props.routine && withSets.length) {
        const routine: Routine = {
          id: newId(), name: session.name, createdAt: nowIso(),
          exercises: withSets.map((r) => ({ exerciseId: r.exerciseId, sets: r.sets.length })),
        };
        await saveRoutine(routine);
      }
      notify('Session logged');
      props.onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      title="Log session"
      onClose={props.onClose}
      footer={<button class="btn btn-primary btn-full" disabled={busy} onClick={save}>Save session</button>}
    >
      <Field label="Name">
        <input class="input" style={{ fontFamily: 'var(--sans)' }} value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)} />
      </Field>

      {rows.map((row, ri) => (
        <div key={row.exerciseId + ri} class="card">
          <div class="row-between" style={{ marginBottom: '10px' }}>
            <span class="item-title">{row.name}</span>
            <span class="label">{row.sets.length} sets</span>
          </div>
          {row.sets.map((set, si) => (
            <div key={si} class="row" style={{ gap: '8px', marginBottom: '8px' }}>
              <input class="input" inputMode="decimal" style={{ width: '88px' }} placeholder={settings.value.weightUnit}
                value={set.weightKg !== undefined ? String(Number(fromKg(set.weightKg).toFixed(1))) : ''}
                onInput={(e) => {
                  const v = Number((e.target as HTMLInputElement).value);
                  updateSet(ri, si, { weightKg: Number.isFinite(v) ? toKg(v) : undefined });
                }} />
              <input class="input grow" inputMode="numeric" placeholder="reps"
                value={set.reps !== undefined ? String(set.reps) : ''}
                onInput={(e) => {
                  const v = Number((e.target as HTMLInputElement).value);
                  updateSet(ri, si, { reps: Number.isFinite(v) ? v : undefined });
                }} />
              <button class="btn btn-sm" onClick={() => removeSet(ri, si)}>−</button>
            </div>
          ))}
          <button class="btn btn-sm btn-full" onClick={() => addSet(ri)}>Add set</button>
        </div>
      ))}

      <div class="row" style={{ gap: '8px' }}>
        <input class="input grow" style={{ fontFamily: 'var(--sans)' }} placeholder="Add an exercise"
          value={newExercise}
          onInput={(e) => setNewExercise((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void addExercise(); }} />
        <button class="btn" onClick={() => void addExercise()}>Add</button>
      </div>
    </Sheet>
  );

  function addSet(ri: number) {
    const last = rows[ri]!.sets[rows[ri]!.sets.length - 1];
    setRows(rows.map((r, i) => i === ri
      ? { ...r, sets: [...r.sets, { ...(last ?? {}), doneAt: nowIso() }] }
      : r));
  }
  function updateSet(ri: number, si: number, patch: Partial<LoggedSet>) {
    setRows(rows.map((r, i) => i === ri
      ? { ...r, sets: r.sets.map((s, j) => (j === si ? { ...s, ...patch } : s)) }
      : r));
  }
  function removeSet(ri: number, si: number) {
    setRows(rows.map((r, i) => i === ri ? { ...r, sets: r.sets.filter((_, j) => j !== si) } : r));
  }
}
